'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSession } from '@/lib/auth-client';
import { ymGoal } from '@/components/yandex-metrika';
import type { AuthModalMode } from '@/contexts/modal-context';
import { AuthModalContent, GUEST_PENDING_MESSAGE_KEY, GUEST_PENDING_TTL_MS } from '@/components/auth/AuthModalContent';

// Re-export layout-level constants so existing consumers
// (`chat-core.tsx`, `hooks/use-streaming-handler.ts`,
// `lib/guest-mode/error-routing.ts`, tests, …) keep working
// without path changes.
export { GUEST_PENDING_MESSAGE_KEY, GUEST_PENDING_TTL_MS };
export interface AuthModalProps {
  forceOpen?: boolean;
  mode?: AuthModalMode;
  /** Called on mount with the pending message text if localStorage has a fresh entry. */
  onPendingMessage?: (text: string) => void;
  /** Called when the user closes the modal (X button or outside click). */
  onDismiss?: () => void;
}

/**
 * Thin Dialog wrapper around `AuthModalContent`. Owns: open/close, session
 * auto-hide, dismiss state, localStorage cleanup for guest-limit flow.
 * The actual layout (headline + OAuth buttons + optional SMS + legal) lives in
 * `AuthModalContent` so `/sign-in` and `/sign-up` pages render identically
 * without the Dialog chrome.
 */
export function AuthModal(t0) {
  const $ = _c(18);
  const {
    forceOpen: t1,
    mode: t2,
    onPendingMessage,
    onDismiss
  } = t0;
  const forceOpen = t1 === undefined ? false : t1;
  const mode = t2 === undefined ? "default" : t2;
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useIsMobile();
  const {
    data: session
  } = useSession();
  const okno1Fired = useRef(false);
  const willShow = forceOpen || !(dismissed || session);
  let t3;
  let t4;
  if ($[0] !== willShow) {
    t3 = () => {
      if (willShow && !okno1Fired.current) {
        okno1Fired.current = true;
        ymGoal("Okno1");
      }
    };
    t4 = [willShow];
    $[0] = willShow;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  if (!forceOpen && (dismissed || session)) {
    return null;
  }
  let t5;
  if ($[3] !== mode || $[4] !== onDismiss || $[5] !== session) {
    t5 = () => {
      if (mode === "guest-limit" && typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(GUEST_PENDING_MESSAGE_KEY);
        } catch {}
      }
      if (mode === "returning" && typeof window !== "undefined") {
        window.location.href = "/";
        return;
      }
      if (onDismiss) {
        onDismiss();
        setDismissed(true);
        return;
      }
      if (session) {
        setDismissed(true);
        return;
      }
      window.location.href = "/";
    };
    $[3] = mode;
    $[4] = onDismiss;
    $[5] = session;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleClose = t5;
  const smsEnabled = process.env.NEXT_PUBLIC_SMS_AUTH_ENABLED === "true";
  const t6 = isMobile ? "w-[92vw] max-w-[420px] p-6 gap-0 rounded-3xl border border-slate-200/70 dark:border-zinc-800 shadow-[0_8px_32px_rgba(15,23,42,0.12),0_24px_72px_rgba(15,23,42,0.16)]" : "sm:max-w-[480px] p-10 gap-0 rounded-3xl border border-slate-200/70 dark:border-zinc-800 shadow-[0_8px_32px_rgba(15,23,42,0.10),0_32px_96px_rgba(15,23,42,0.18)]";
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <VisuallyHiddenPrimitive.Root><DialogTitle>Войти или зарегистрироваться</DialogTitle><DialogDescription>Выберите способ входа — Google, Яндекс, Telegram или ВКонтакте.</DialogDescription></VisuallyHiddenPrimitive.Root>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <X className="size-4" />;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== handleClose) {
    t9 = <button onClick={handleClose} className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/70 bg-white/80 text-slate-500 transition-all hover:border-slate-300 hover:bg-white hover:text-slate-900 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-400 dark:hover:border-zinc-700 dark:hover:bg-zinc-900 dark:hover:text-zinc-100" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"}>{t8}</button>;
    $[9] = handleClose;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== mode || $[12] !== onPendingMessage) {
    t10 = <AuthModalContent mode={mode} smsEnabled={smsEnabled} onPendingMessage={onPendingMessage} />;
    $[11] = mode;
    $[12] = onPendingMessage;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t6 || $[16] !== t9) {
    t11 = <Dialog open={true} onOpenChange={_temp}><DialogContent className={t6} showCloseButton={false}>{t7}{t9}{t10}</DialogContent></Dialog>;
    $[14] = t10;
    $[15] = t6;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
function _temp() {}